import React from 'react';
import { IonItem, IonLabel } from '@ionic/react';
import { formatInTimeZone } from 'date-fns-tz';
import { useSmartCase } from '@/modules/footer';
import SectionCard from '@/components/SectionCard';
import {
  datetimeAsDateObj,
  formatTopicDate,
  formatTopicTime,
} from '@/modules/topic/datetime';
import {
  formatTimezoneLabel,
  normalizeTimezone,
} from '@/modules/topic/timezone';
import type { Topic } from '@/modules/topic/types';
import styles from './BylineDatelineBlock.module.css';

interface DatelineParts {
  date: string;
  /** '' when the story has no time of day, which it stores as midnight. */
  time: string;
  zone: string;
}

const datelineParts = (topic: Topic): DatelineParts | null => {
  const start = datetimeAsDateObj(topic.startTime);
  if (start === undefined) return null;
  try {
    const tz = normalizeTimezone(topic.timezone);
    const hasTime = formatInTimeZone(start, tz, 'HH:mm') !== '00:00';
    return {
      date: formatTopicDate(start, tz),
      time: hasTime ? formatTopicTime(start, tz) : '',
      zone: hasTime ? formatTimezoneLabel(tz, start) : '',
    };
  } catch {
    return null;
  }
};

/** A row of the card: its label on the left, its value on the right. */
const Row: React.FC<{
  label: string;
  value: string;
  last?: boolean;
  testId: string;
}> = ({ label, value, last = false, testId }) => (
  <IonItem lines={last ? 'none' : 'inset'}>
    <IonLabel>{label}</IonLabel>
    <span slot="end" className={styles.value} data-testid={testId}>
      {value}
    </span>
  </IonItem>
);

/**
 * The read view of a news story's Byline and Dateline cards, matching what
 * the news edit form sets: who the story is credited to, then the day it was
 * filed, its time and zone when it has a time of day, and where. A story has
 * no end, so there is no end date to show. The Byline card is left out when
 * the story credits no one.
 */
const BylineDatelineBlock: React.FC<{ topic: Topic }> = ({ topic }) => {
  const byline = topic.byline?.trim() ?? '';
  const location = topic.location?.trim() ?? '';
  const parts = datelineParts(topic);
  // Row labels are written in sentence case and cased for the Ionic mode, as
  // in the Where & when card: title case in ios mode, as written in md.
  const smartCase = useSmartCase();

  const rows = [
    parts && { label: 'Date', value: parts.date, testId: 'dateline-date' },
    parts?.time && {
      label: 'Time',
      value: parts.time,
      testId: 'dateline-time',
    },
    parts?.zone && {
      label: 'Time zone',
      value: parts.zone,
      testId: 'dateline-timezone',
    },
    location && {
      label: 'Location',
      value: location,
      testId: 'dateline-location',
    },
  ].filter(Boolean) as { label: string; value: string; testId: string }[];

  if (!byline && rows.length === 0) return null;

  return (
    <div data-testid="byline-dateline-view">
      {byline && (
        <SectionCard title="Byline">
          <IonItem lines="none">
            <IonLabel className={styles.byline} data-testid="byline-name">
              {byline}
            </IonLabel>
          </IonItem>
        </SectionCard>
      )}
      {rows.length > 0 && (
        <SectionCard title="Dateline">
          {rows.map((row, i) => (
            <Row
              key={row.testId}
              {...row}
              label={smartCase(row.label)}
              last={i === rows.length - 1}
            />
          ))}
        </SectionCard>
      )}
    </div>
  );
};

export default BylineDatelineBlock;
